import { useMemo } from 'react'
import Link from 'next/link'
import { LinkButton } from '@/atoms/Button'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { LightIcon } from '@/atoms/Icons/LightIcon'
import { UsersThreeIcon } from '@/atoms/Icons/UsersThreeIcon'
import { HeadingSM, LabelMD, ParagraphSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useGuildMember } from '@/services/GuildUserService'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export function useExplainerContent() {
  const { t } = useTranslate('about')
  const { isGuildMember } = useGuildMember()
  return useMemo(
    () => [
      {
        background: 'bg-black',
        button: (
          <LinkButton
            className="h-14 w-full md:w-1/3 xl:w-1/2"
            href={isGuildMember ? paths.guild.index : paths.guild.join}
            variant="white"
          >
            <LabelMD>
              {isGuildMember ? t('viewYourDashboard', 'View Your Dashboard') : t('joinTheGuild', 'Join the Guild')}
            </LabelMD>
          </LinkButton>
        ),
        content: (
          <Translate t={t} i18nKey="theAngelGuildBlurbv2">
            <ParagraphSM>
              The Angel Guild is our community of members who’ve joined the mission to bring meaningful, inspiring, and
              worthwhile stories to life. When you join the Guild, you gain access to Angel’s entire library of movies
              and shows, including weekly releases, while helping us expand our library with even more stories that
              uplift and inspire.
            </ParagraphSM>
            <ParagraphSM>
              By supporting Angel, you’re not just enjoying great entertainment—you’re playing a vital role in building
              a collection of films and series that leave a lasting impact. Your continued support helps us create new
              movies and episodes every week, all while empowering storytellers who value what truly matters.
            </ParagraphSM>
            <ParagraphSM>Welcome to Angel—we’re glad you’re here!</ParagraphSM>
          </Translate>
        ),
        icon: <GuildIconMono className="shrink-0" color="white" size={48} />,
        image: 'v1706832342/angel-studios/guild/image_frame.webp',
        title: t('theAngelGuild', 'The Angel Guild'),
      },
      {
        background: 'bg-black',
        content: (
          <Translate t={t} i18nKey={'defineLightBlurbV5'}>
            <ParagraphSM>
              To us, light means stories that are true, honest, noble, just, authentic, lovely, admirable, or excellent.
              Stories that are true to the human experience—that raise, inspire, and move us—deserve to be shared and
              cherished. That’s why Angel is committed to creating authentic films and shows that resonate with real
              people—seeking to address the unspoken needs of audiences.
            </ParagraphSM>
            <ParagraphSM>
              From the emotional journey of{' '}
              <Link className="italic underline" href={paths.movies.soundOfFreedom}>
                The Sound of Freedom
              </Link>{' '}
              to the inspiring courage of{' '}
              <Link className="italic underline" href={paths.movies.bonhoeffer}>
                Bonhoeffer: Pastor. Spy. Assassin.
              </Link>{' '}
              , Angel movies shine a light on stories that speak to the soul. Series like{' '}
              <Link className="italic underline" href={paths.shows.wingfeatherSaga}>
                The Wingfeather Saga
              </Link>{' '}
              and and{' '}
              <Link className="italic underline" href={paths.shows.tuttleTwins}>
                Tuttle Twins
              </Link>{' '}
              inspire young minds and instill values of empathy, courage, and wisdom—proving that even the youngest
              hearts can be touched by stories that are genuine, bold, and meaningful.
            </ParagraphSM>
          </Translate>
        ),
        icon: <LightIcon className="shrink-0" size={48} />,
        image: '/v1703151031/angel-studios/about/c11d0549e4d02094b7dbbddf1d016258.webp',
        subtitle: t('howDoWeDefineLight', 'How do we define light?'),
        title: t('amplifyingLight', 'Amplifying Light'),
      },
      {
        content: (
          <Translate t={t} i18nKey="crowdFundingBlurbv2">
            <HeadingSM weight="bold" className="-mt-4 mb-8">
              The Heart of Angel’s Story
            </HeadingSM>
            <ParagraphSM>
              Crowdfunding isn’t just something we do—it’s part of who we are. When our company faced a legal battle
              that threatened to shut us down, Angel investors stepped in, raising $10 million to help us survive. That
              support didn’t just save us—it shaped us.
            </ParagraphSM>
            <ParagraphSM>
              From the beginning, crowdfunding has fueled many of our most beloved projects, including{' '}
              <Link className="italic underline" href={paths.shows.wingfeatherSaga}>
                The Wingfeather Saga
              </Link>{' '}
              ,{' '}
              <Link className="italic underline" href={paths.shows.tuttleTwins}>
                Tuttle Twins
              </Link>{' '}
              ,{' '}
              <Link className="italic underline" href={paths.movies.theShift}>
                The Shift
              </Link>{' '}
              ,{' '}
              <Link className="italic underline" href={paths.watch.david}>
                David
              </Link>{' '}
              , and{' '}
              <Link className="italic underline" href={paths.shows.riotAndTheDance}>
                The Riot and the Dance
              </Link>
              . Just as people once crowdfunded the Statue of Liberty’s torch, Angel investors have helped bring
              powerful stories to audiences worldwide.
            </ParagraphSM>
            <ParagraphSM>
              Today, crowdfunding is not just used to fund projects but to invite the community into the success of our
              shows. Our name itself—Angel—honors the fans who believed in us when it mattered most. You saved our
              company. And together, we’re building a future where audiences decide what stories get told.
            </ParagraphSM>
          </Translate>
        ),
        icon: <UsersThreeIcon className="shrink-0" color="white" size={48} />,
        image: '/v1706128612/angel-studios/about/bf3019fc368e9bf0d9fc25ae7fdb064c.webp',
        reverse: true,
        title: t('crowdfunding', 'Crowdfunding'),
      },
    ],
    [t, isGuildMember],
  )
}
